import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  SpeakerWaveIcon,
  ClockIcon,
  CheckIcon,
  XMarkIcon,
  SparklesIcon,
  CpuChipIcon
} from '@heroicons/react/24/outline'
import { useAuthStore } from '@/store/auth'
import toast from 'react-hot-toast'

// ============================================================================
// PRECISION BRUTALIST Device Info Collection
// ============================================================================

interface DeviceInfo {
  phone: string
  laptop: string
  earphones: string
  wearable: string
}

interface DeviceInfoCollectionProps {
  onComplete: (deviceInfo: DeviceInfo) => void
  onSkip: () => void
}

const deviceCategories = [
  {
    id: 'phone',
    label: 'Smartphone',
    icon: DevicePhoneMobileIcon,
    placeholder: 'e.g., iPhone 15 Pro, Galaxy S24',
    examples: ['iPhone 15 Pro', 'Samsung Galaxy S24', 'Google Pixel 8', 'OnePlus 12']
  },
  {
    id: 'laptop',
    label: 'Laptop',
    icon: ComputerDesktopIcon,
    placeholder: 'e.g., MacBook Pro M3, ThinkPad X1',
    examples: ['MacBook Pro M3', 'Dell XPS 13', 'ThinkPad X1 Carbon', 'Surface Laptop']
  },
  {
    id: 'earphones',
    label: 'Audio',
    icon: SpeakerWaveIcon,
    placeholder: 'e.g., AirPods Pro, Sony XM5',
    examples: ['AirPods Pro 2', 'Sony WH-1000XM5', 'Bose QC Ultra', 'Galaxy Buds Pro']
  },
  {
    id: 'wearable',
    label: 'Wearable',
    icon: ClockIcon,
    placeholder: 'e.g., Apple Watch Ultra 2',
    examples: ['Apple Watch Ultra 2', 'Galaxy Watch 6', 'Garmin Fenix 7', 'Oura Ring']
  }
]

const ease = [0.16, 1, 0.3, 1]

export function DeviceInfoCollection({ onComplete, onSkip }: DeviceInfoCollectionProps) {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({
    phone: '',
    laptop: '',
    earphones: '',
    wearable: ''
  })
  const [currentField, setCurrentField] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { user, token } = useAuthStore()

  const handleDeviceChange = (category: string, value: string) => {
    setDeviceInfo(prev => ({ ...prev, [category]: value }))
  }

  const handleSubmit = async () => {
    const hasAtLeastOneDevice = Object.values(deviceInfo).some(value => value.trim() !== '')

    if (!hasAtLeastOneDevice) {
      toast.error('Please add at least one device')
      return
    }

    setIsSubmitting(true)

    try {
      const currentToken = token || localStorage.getItem('jpdz_token')

      if (!currentToken) {
        toast.error('Authentication required')
        onComplete(deviceInfo)
        return
      }

      const response = await fetch('/api/device/device-info', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        },
        body: JSON.stringify(deviceInfo)
      })

      if (response.ok) {
        toast.success('Devices saved!')
        onComplete(deviceInfo)
      } else if (response.status === 401) {
        toast.error('Session expired')
        localStorage.removeItem('jpdz_token')
        onComplete(deviceInfo)
      } else {
        throw new Error('Failed to save')
      }
    } catch (error) {
      toast.error('Failed to save. You can update this later.')
      onComplete(deviceInfo)
    } finally {
      setIsSubmitting(false)
    }
  }

  const getFilledCount = () => Object.values(deviceInfo).filter(v => v.trim() !== '').length

  const selectExample = (category: string, example: string) => {
    handleDeviceChange(category, example)
    setCurrentField(null)
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-[#030303] overflow-hidden"
      >
        {/* Ambient background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-accent/[0.04] rounded-full blur-[150px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-orange-500/[0.03] rounded-full blur-[120px]" />
        </div>

        {/* Large background text */}
        <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-display font-black text-[20vw] leading-none text-white/[0.015] pointer-events-none select-none">
          TECH
        </span>

        {/* Main Content */}
        <div className="relative z-10 w-full max-w-5xl mx-auto px-6 h-full overflow-y-auto py-12">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, ease }}
            className="text-center mb-12"
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="w-20 h-20 bg-gradient-to-br from-accent to-orange-600 flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-accent/30"
            >
              <CpuChipIcon className="w-10 h-10 text-black" />
            </motion.div>

            <h1 className="font-display font-black text-4xl md:text-5xl lg:text-6xl text-white mb-4 tracking-tight">
              Tell us about your<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-orange-500">tech</span>
            </h1>

            <p className="font-body text-lg text-white/40 max-w-lg mx-auto">
              Help us personalize your experience by sharing the devices you use
            </p>

            {user && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-accent/10 border border-accent/20"
              >
                <SparklesIcon className="w-4 h-4 text-accent" />
                <span className="font-mono text-xs text-accent">
                  Hey {user.username}! This helps AI give you better recommendations
                </span>
              </motion.div>
            )}
          </motion.div>

          {/* Progress */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, ease }}
            className="mb-10"
          >
            <div className="flex items-center justify-between mb-3 max-w-2xl mx-auto">
              <span className="font-mono text-[10px] tracking-[0.2em] text-white/30 uppercase">
                Devices Added
              </span>
              <span className="font-mono text-sm text-accent font-bold">
                {getFilledCount()}/{deviceCategories.length}
              </span>
            </div>
            <div className="h-1 bg-white/[0.06] overflow-hidden max-w-2xl mx-auto">
              <motion.div
                animate={{ width: `${(getFilledCount() / deviceCategories.length) * 100}%` }}
                className="h-full bg-gradient-to-r from-accent to-orange-500"
                transition={{ duration: 0.4, ease }}
              />
            </div>
          </motion.div>

          {/* Device Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, ease }}
            className="grid md:grid-cols-2 gap-4 mb-12 max-w-4xl mx-auto"
          >
            {deviceCategories.map((category, idx) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 + idx * 0.05, ease }}
                className="group"
              >
                <div className={`bg-[#0a0a0a] border transition-all p-6 ${currentField === category.id
                    ? 'border-accent/40'
                    : 'border-white/[0.06] hover:border-white/[0.1]'
                  }`}>
                  {/* Category Header */}
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-12 h-12 flex items-center justify-center transition-colors ${deviceInfo[category.id as keyof DeviceInfo]
                        ? 'bg-accent text-black'
                        : 'bg-white/[0.03] text-white/40'
                      }`}>
                      <category.icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-display font-semibold text-lg text-white">{category.label}</h3>
                      {deviceInfo[category.id as keyof DeviceInfo] && (
                        <motion.span
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="font-mono text-[9px] tracking-wider text-accent uppercase flex items-center gap-1"
                        >
                          <CheckIcon className="w-3 h-3" />
                          Added
                        </motion.span>
                      )}
                    </div>
                  </div>

                  {/* Input */}
                  <div className="relative">
                    <input
                      type="text"
                      value={deviceInfo[category.id as keyof DeviceInfo]}
                      onChange={(e) => handleDeviceChange(category.id, e.target.value)}
                      onFocus={() => setCurrentField(category.id)}
                      onBlur={() => setTimeout(() => setCurrentField(null), 150)}
                      placeholder={category.placeholder}
                      className="w-full bg-black border border-white/[0.08] px-4 py-3.5 text-white text-sm placeholder-white/20 focus:border-accent/50 focus:outline-none transition-colors font-body"
                    />
                    {deviceInfo[category.id as keyof DeviceInfo] && (
                      <button
                        onClick={() => handleDeviceChange(category.id, '')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white transition-colors"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* Quick Examples */}
                  <AnimatePresence>
                    {currentField === category.id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-3 overflow-hidden"
                      >
                        <p className="font-mono text-[9px] tracking-wider text-white/20 uppercase mb-2">
                          Quick select
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {category.examples.map((example) => (
                            <button
                              key={example}
                              onClick={() => selectExample(category.id, example)}
                              className="px-3 py-1.5 bg-white/[0.03] border border-white/[0.06] hover:border-accent/40 hover:bg-accent/5 text-white/50 hover:text-white font-mono text-[11px] transition-all"
                            >
                              {example}
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, ease }}
            className="flex flex-col sm:flex-row justify-center items-center gap-4 max-w-lg mx-auto"
          >
            <motion.button
              onClick={onSkip}
              whileHover={{ x: -4 }}
              whileTap={{ scale: 0.98 }}
              className="px-8 py-4 text-white/40 hover:text-white font-display font-medium transition-colors w-full sm:w-auto"
            >
              Skip for now
            </motion.button>

            <motion.button
              onClick={handleSubmit}
              disabled={isSubmitting || getFilledCount() === 0}
              whileHover={{ scale: getFilledCount() > 0 ? 1.02 : 1 }}
              whileTap={{ scale: getFilledCount() > 0 ? 0.98 : 1 }}
              className={`px-10 py-4 font-display font-bold flex items-center justify-center gap-3 w-full sm:w-auto transition-all ${getFilledCount() > 0
                  ? 'bg-gradient-to-r from-accent to-orange-500 text-black shadow-xl shadow-accent/25'
                  : 'bg-white/[0.05] text-white/30 cursor-not-allowed'
                }`}
            >
              {isSubmitting ? (
                <>
                  <motion.div
                    className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <span>
                    {getFilledCount() > 0
                      ? `Save ${getFilledCount()} Device${getFilledCount() > 1 ? 's' : ''}`
                      : 'Add at least one'
                    }
                  </span>
                  {getFilledCount() > 0 && <CheckIcon className="w-5 h-5" />}
                </>
              )}
            </motion.button>
          </motion.div>

          {/* Hint */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="font-mono text-[10px] text-white/15 text-center mt-8 max-w-md mx-auto"
          >
            This helps our AI understand your tech ecosystem for better recommendations
          </motion.p>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}